import { toast } from 'react-toastify';
import type { EntityData } from '@ContractBuilder/types';
import type { QueryFilter } from '@domain/types/filters';
import { getRequestAuthPayload } from '@helpers/getRequestAuth';
import { DocumentMode } from '@root/@types/types';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import axios from 'axios';

import { getResourcesListToFetch } from '../utils/get-resources-list-to-fetch';

import type { QueryFactory } from './factory.types';
import { fetchEntity } from './fetchEntity';
import { serializeQueryFilters } from './serialize-query-filters';

export const SUBMISSIONS_OPENSEARCH_PAGE_SIZE = 50;

export interface SubmissionShort extends Pick<
  EntityData,
  | 'branding'
  | 'branding_id'
  | 'class_of_business'
  | 'created_at'
  | 'created_by'
  | 'endorsementsCount'
  | 'guidanceFindingsCount'
  | 'guidanceReviewedCount'
  | 'id'
  | 'last_updated_by'
  | 'name'
  | 'ref'
  | 'sourceResourceDisplayName'
  | 'sourceResourceId'
  | 'sourceResourceName'
  | 'status'
  | 'tags'
  | 'teams'
  | 'transaction_status'
  | 'unresolvedCommentsCount'
  | 'updated_at'
> {
  umr?: string;
}

export const fetchSubmissions = async (): Promise<SubmissionShort[]> => {
  let data: any[] = [];
  let requestCount = 0;
  const maxRequests = 40;

  return await (async function doFetch(cursor?: string): Promise<SubmissionShort[]> {
    // safeguard against infinite loops caused by API bugs (could affect billing)
    if (requestCount >= maxRequests) {
      toast.error('Maximum request limit reached', {
        autoClose: false,
      });
      return [];
    }

    requestCount++;

    const networkPayload = await getRequestAuthPayload({
      method: 'get',
      url: '/submissions',
      params: { cursor, source: 'ddb' },
    });

    const response = await axios(networkPayload);
    data = [...data, ...response.data.data];
    if (response.data.nextCursor) {
      return await doFetch(response.data.nextCursor);
    }

    return data;
  })();
};

export const getSortField = (sortField?: string) => {
  if (sortField === 'commentsCount') {
    return 'unresolvedCommentsCount';
  }
  if (sortField === 'missingDatapointsCount') {
    return 'contract_missing_data_count';
  }
  return sortField;
};

export const fetchSubmissionsOpensearch = async (params?: {
  size?: number;
  sortField?: string;
  sortOrder?: string;
  queryFilters?: QueryFilter[];
  search?: string;
  cursor?: string;
  pitId?: string;
}): Promise<{
  data: EntityData[];
  count?: number;
  createdByUsers?: string[];
  updatedByUsers?: string[];
  templates?: string[];
  hasFinalisedContracts: boolean;
  pitId?: string;
  nextCursor?: string;
}> => {
  const { size, sortField, sortOrder, search, cursor, pitId } = params ?? {};

  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/submissions',
    params: {
      size,
      sortField: getSortField(sortField),
      sortOrder,
      search,
      cursor,
      pitId,
      filters: serializeQueryFilters(params?.queryFilters ?? []),
    },
  });

  const response = await axios(networkPayload);
  return response.data;
};

// Count-only variant (used for saved-view badges): the backend fast path skips the PIT,
// aggregations and row fetching and returns just the matching-total for the filter set.
export const fetchSubmissionsCount = async (queryFilters: QueryFilter[]): Promise<number> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/submissions',
    params: {
      count_only: 'true',
      filters: serializeQueryFilters(queryFilters),
    },
  });

  const response = await axios(networkPayload);
  return response.data.count ?? 0;
};

export const fetchSubmission = async (id: string): Promise<EntityData> => {
  const params = new URLSearchParams({ include: ['attachments', 'blocks'].join(',') });

  const networkPayload = await getRequestAuthPayload({ method: 'get', url: `/submissions/${id}`, params });

  const response = await axios(networkPayload);
  return response.data;
};

export const submissionQueries = {
  listOpensearch: ({
    sortField,
    sortOrder,
    queryFilters,
    search,
  }: {
    sortField?: string;
    sortOrder?: string;
    queryFilters?: QueryFilter[];
    search?: string;
  }) =>
    infiniteQueryOptions({
      queryKey: ['submissions', sortField, sortOrder, queryFilters, search],
      queryFn: async ({ pageParam }) => {
        const data = await fetchSubmissionsOpensearch({
          cursor: pageParam?.nextCursor,
          pitId: pageParam?.pitId,
          size: SUBMISSIONS_OPENSEARCH_PAGE_SIZE,
          sortField,
          sortOrder,
          queryFilters,
          search,
        });
        return { ...data, pitId: data.pitId ?? '', nextCursor: data.nextCursor ?? '' };
      },
      getNextPageParam: (lastPage) => {
        return lastPage.nextCursor ? { pitId: lastPage.pitId, nextCursor: lastPage.nextCursor } : undefined;
      },
      initialPageParam: { pitId: '', nextCursor: '' },
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    }),
  list: () =>
    queryOptions({
      queryKey: ['submissions', 'list'] as const,
      queryFn: fetchSubmissions,
    }),
  detail: (id: string) =>
    queryOptions({
      queryKey: ['submissions', 'detail', id] as const,
      queryFn: () =>
        fetchEntity({
          dataRange: getResourcesListToFetch(),
          documentMode: DocumentMode.SUBMISSIONS,
          entityId: id,
        }),
    }),
  forRenewalParent: (id: string) =>
    queryOptions({
      queryKey: ['renewal-parent', id] as const,
      queryFn: () => fetchSubmission(id),
      staleTime: Infinity,
    }),
  forEndorsementParent: (id: string) =>
    queryOptions({
      queryKey: ['endorsement-parent', id] as const,
      queryFn: () =>
        fetchEntity({
          dataRange: getResourcesListToFetch(),
          documentMode: DocumentMode.SUBMISSIONS,
          entityId: id,
        }),
    }),
} satisfies QueryFactory;

export function useSubmissionsOpensearch({
  sortField,
  sortOrder,
  queryFilters,
  search,
}: {
  sortField?: string;
  sortOrder?: string;
  queryFilters?: QueryFilter[];
  search?: string;
}) {
  return useInfiniteQuery(submissionQueries.listOpensearch({ sortField, sortOrder, queryFilters, search }));
}

export function useSubmissions() {
  return useQuery(submissionQueries.list());
}

export const fetchSubmissionsCSVDownloadUrl = async (): Promise<string> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/submissions',
    params: { format: 'csv' },
  });

  const response = await axios<{ downloadUrl: string }>(networkPayload);
  return response.data.downloadUrl;
};
